import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { I, C } from './icones';
import Item from './Item';
import { libelleSection } from '../../i18n/menu';

/**
 * Icone par section. Le backend ne renvoie d'icone que sur les ENTREES, pas sur
 * les sections - la correspondance se fait donc ici, sur le titre servi par
 * `/v1/navigation`. Un titre inconnu (section ajoutee par migration sans passer
 * ici) retombe sur `Folder` : une section sans icone casserait l'alignement de
 * toutes les autres, un pictogramme neutre non.
 */
const ICONE_SECTION = {
  'Pilotage': 'Chart',
  'Messagerie interne': 'MessagesSquare',
  'Sinistres': 'Alert',
  'Finance': 'Money',
  'Attestations': 'Cert',
  'Courrier & GED': 'Mail',
  'Droits & accès': 'Shield',
  'Administration': 'Settings',
};

export default function Section({ titre, items, premiere, ouverte, basculer, compact = false, ...passthrough }) {
  const { t } = useTranslation('commun');
  const [survol, setSurvol] = useState(false);
  // En mode compact il n'y a pas de place pour un titre : la section devient
  // un simple filet de separation, et toutes ses entrees restent visibles.
  if (compact) {
    return (
      <div style={{
        borderTop: premiere ? 'none' : `1px solid ${C.border}`,
        paddingTop: premiere ? 0 : 6, marginTop: premiere ? 0 : 6,
      }}>
        {items.map((item) => <Item key={item.cle} item={item} compact {...passthrough} />)}
      </div>
    );
  }

  // Une section sans titre (cas theorique : entrees sans section) n'a rien a
  // replier - on la rend telle quelle pour ne jamais masquer d'entree.
  if (!titre) {
    return <div>{items.map((item) => <Item key={item.cle} item={item} {...passthrough} />)}</div>;
  }

  // Quand la section est repliee, ses items (dont celui qui porte le badge
  // mail, ex. « Messagerie ») ne sont pas rendus, donc le badge disparait du
  // DOM. On le fait remonter sur l'en-tete de section dans ce cas precis :
  // repliee ET au moins un item porte le badge mail avec un compteur > 0.
  // Ouverte, l'entree elle-meme affiche deja son badge - on ne le double pas.
  const nonLusMail = passthrough.nonLusMail || 0;
  const afficherCompteurMail = !ouverte && nonLusMail > 0 && items.some((item) => item.badge === 'mail');
  const nonLusConversations = passthrough.nonLusConversations || 0;
  const afficherCompteurConversations = !ouverte && nonLusConversations > 0 && items.some((item) => item.badge === 'conversations');
  const demandesAppareils = passthrough.demandesAppareils || 0;
  const afficherCompteurAppareils = !ouverte && demandesAppareils > 0 && items.some((item) => item.badge === 'appareils');
  const declarationsCorbeille = passthrough.declarationsCorbeille || 0;
  const afficherCompteurCorbeille = !ouverte && declarationsCorbeille > 0 && items.some((item) => item.badge === 'declarations');
  const IconeSection = I[ICONE_SECTION[titre]] || I.Folder;

  return (
    <div>
      <div
        onClick={() => basculer(titre)}
        role="button"
        tabIndex={0}
        aria-expanded={ouverte}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); basculer(titre); }
        }}
        onMouseEnter={() => setSurvol(true)}
        onMouseLeave={() => setSurvol(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 11, cursor: 'pointer',
          // En-tete de section en CARTE, comme les entrees qu'elle contient.
          // L'ancien rendu - petites capitales grises a 55 % d'opacite, sans fond -
          // etait a la fois illisible et impossible a distinguer d'un simple
          // intertitre : rien ne disait que la ligne etait cliquable.
          fontSize: 14, fontWeight: 600, letterSpacing: 0,
          color: '#fff',
          background: ouverte
            ? 'rgba(255,255,255,0.14)'
            : survol ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)',
          borderRadius: 10,
          padding: '9px 12px',
          marginTop: premiere ? 0 : 8,
          marginBottom: ouverte ? 6 : 0,
          transition: 'background 0.15s',
          userSelect: 'none',
        }}
      >
        <span
          aria-hidden
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
            background: 'rgba(255,255,255,0.12)',
            color: 'rgba(255,255,255,0.92)',
          }}
        >
          <IconeSection />
        </span>
        <span style={{
          flex: 1, minWidth: 0,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {libelleSection(titre)}
        </span>
        {afficherCompteurMail && (
          <span
            aria-label={t('sidebarSection.messagesNonLus', { count: nonLusMail })}
            style={{
              // Blanc plein sur vert : le badge portait le vert de la barre
              // elle-meme, il ne se detachait que par son texte.
              background: '#fff', color: C.bg, borderRadius: 10,
              fontSize: 10.5, fontWeight: 800, padding: '1px 7px', minWidth: 20,
              textAlign: 'center', flexShrink: 0,
            }}
          >{nonLusMail > 99 ? '99+' : nonLusMail}</span>
        )}
        {afficherCompteurConversations && (
          <span
            aria-label={t('sidebarSection.messagesNonLus', { count: nonLusConversations })}
            style={{
              background: '#ef4444', color: '#fff', borderRadius: 10,
              fontSize: 10.5, fontWeight: 800, padding: '1px 7px', minWidth: 20,
              textAlign: 'center', flexShrink: 0,
            }}
          >{nonLusConversations > 99 ? '99+' : nonLusConversations}</span>
        )}
        {afficherCompteurAppareils && (
          <span
            aria-label={t('sidebarSection.appareilsAValider', { count: demandesAppareils })}
            style={{
              background: '#f59e0b', color: '#fff', borderRadius: 10,
              fontSize: 10.5, fontWeight: 800, padding: '1px 7px', minWidth: 20,
              textAlign: 'center', flexShrink: 0,
            }}
          >{demandesAppareils > 99 ? '99+' : demandesAppareils}</span>
        )}
        {afficherCompteurCorbeille && (
          <span
            aria-label={t('sidebarSection.declarationsEnAttente', { count: declarationsCorbeille })}
            style={{
              background: '#f59e0b', color: '#fff', borderRadius: 10,
              fontSize: 10.5, fontWeight: 800, padding: '1px 7px', minWidth: 20,
              textAlign: 'center', flexShrink: 0,
            }}
          >{declarationsCorbeille > 99 ? '99+' : declarationsCorbeille}</span>
        )}
        <span style={{
          display: 'flex', color: 'rgba(255,255,255,0.7)', flexShrink: 0,
          transform: ouverte ? 'none' : 'rotate(-90deg)',
          transition: 'transform 0.2s',
        }}>
          <I.Chevron />
        </span>
      </div>
      {ouverte && items.map((item) => <Item key={item.cle} item={item} {...passthrough} />)}
    </div>
  );
}
